// Datos del Portal del Vendedor (comp 05 · mock `portal-vendedor.html`): KPIs del Home, «Mis
// ventas» y las alertas. Todo sale del silo — lo que el silo no calcula NO se muestra, en vez de
// pintarse en cero.
import { useEffect, useMemo } from 'react';
import {
    infiniteQueryOptions,
    useInfiniteQuery,
    useQuery,
    type InfiniteData,
    type UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { wizardApi } from '@/api/services/retail-wizard';
import type {
    SaleLine,
    SalePaymentDetail,
    SaleStatus,
    SaleSummary,
    SalesPage,
    SellerHomeTotals,
} from '@/api/services/retail-wizard/contract';
import type { ExternalProviderEnum, SaleTypeEnum } from '@/domains/sales/wizard/enums';
import { formatShortDateMx, formatTimeMx, daysSinceMx } from '@/lib/dateHelpers';
import { formatPhone } from '@/shared/lib/format/phone';
import { toPaymentLines, type PaymentLine } from '@/shared/lib/format/salePaymentLines';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import { hasEquipment, NO_PHONE_LABEL } from '@/shared/patterns/SaleTicket/ticketSummary';
import { formatStorage } from '@/shared/lib/format/capacity';

/** Query keys del portal. Exportadas para que quien invalide no las copie a mano. */
export const PORTAL_KEYS = {
    kpis: ['sales', 'kpis'] as const,
    recentSales: ['sales', 'recent'] as const,
    storeSales: ['sales', 'store'] as const,
    sellerAlerts: ['sales', 'home'] as const,
};

export interface SellerKpis {
    salesTodayCount: number;
    salesTodayCents: number;
    /** 0 = el silo no tiene meta configurada; la pantalla no puede calcular ningún porcentaje. */
    dailyGoalCents: number;
    /** Ranking dentro de la tienda, por monto vendido hoy. 0 = todavía sin posición (no vendió). */
    rankingPosition: number;
    rankingTotal: number;
    prospectsTodayCount: number;
    prospectsOverdueCount: number;
}

export interface RecentSale {
    /** Clave estable de la fila y del deep-link: el folio puede faltar, el id nunca. */
    saleId: string;
    saleRef: string;
    /** SKU vendido — cruza con el catálogo para el "top de mi tienda hoy"; `null` sin teléfono. */
    sku: string | null;
    customerName: string;
    /** Nombre comercial del equipo; cae al SKU cuando la venta no lo trae. */
    product: string;
    /** Marca sola, para el filtro por producto: la etiqueta comercial no se puede desarmar. */
    productBrand: string | null;
    productModel: string | null;
    productCapacity: string | null;
    /**
     * Precio del equipo colocado. `null` en las ventas anteriores al campo: lo cobrado en caja es
     * otra magnitud (en crédito es el enganche y en la derivación externa vale 0), así que la
     * pantalla pinta un guion en vez de un número que no es ese precio.
     */
    equipmentPriceCents: number | null;
    /** Lo COBRADO en caja: enganche más contado en crédito, el total en contado. */
    amountCents: number;
    /** Lo vendido (DEC-46): monto, ticket y volumen de Mis ventas y del Home. */
    soldCents?: number;
    downPaymentCents: number | null;
    method: 'CASH' | 'CARD' | 'SPEI' | null;
    paymentDetail: SalePaymentDetail | null;
    /** Pagos del cobro; `null` en ventas anteriores al campo. */
    payments?: PaymentLine[] | null;
    /** Cómo se financió la venta; con `externalProvider` resuelve la financiera de la fila. */
    type: SaleTypeEnum;
    /** Financiera externa. `null` en contado y en el crédito propio de SureKeep. */
    externalProvider: ExternalProviderEnum | null;
    creditId: string | null;
    imei: string | null;
    retailCustomerId: string | null;
    customerCurp: string | null;
    sellerRetailUserId: string;
    sellerName: string | null;
    storeName: string | null;
    zoneName: string | null;
    status: SaleStatus;
    cancelledAt: string | null;
    cancelReason: string | null;
    centralSyncStatus: string;
    /** ISO crudo de la venta: el corte semanal y la bitácora necesitan la fecha, no la etiqueta. */
    soldAt: string;
    at: string;
    /** Hora del día (HH:mm) para las filas densas del Home. */
    atTime: string;
    /** `true` si la venta se cerró hoy (el Home solo cuenta las de hoy). */
    today: boolean;
    /** Líneas con sus identificadores. Ausente en las ventas anteriores a las líneas. */
    lines?: SaleLine[];
    /** Promo de venta congelada al cerrar; ausente si la venta no tuvo. */
    salePromotionId?: string;
    salePromotionVersion?: number;
}

/** Alerta accionable del Home (mock §renderAlertas). El prospecto "prometió pasar hoy" se deriva
 *  de los prospectos reales en la pantalla; aquí viven las que no salen de ahí. */
/** Alerta del piso, informativa: el vendedor no resuelve ninguno de los dos motivos. */
export interface SellerAlert {
    id: string;
    /** Teléfono del comprador, ya agrupado: la alerta del silo no trae nombre. */
    customerPhone: string;
    text: string;
}

/**
 * KPIs REALES del silo (`GET /seller/home`).
 *
 * Solo viven aquí los que el silo calcula. La comisión, la promo y el turno se quitaron de la
 * pantalla en vez de quemarse en cero: un cero falso sobre el dinero del vendedor es peor que la
 * ausencia del dato.
 */
export function useSellerKpis() {
    return useQuery({
        queryKey: PORTAL_KEYS.kpis,
        queryFn: async (): Promise<SellerKpis> => {
            const home = await wizardApi.getSellerHome();
            return {
                salesTodayCount: home.salesTodayCount,
                salesTodayCents: home.soldTodayCents,
                dailyGoalCents: home.dailyGoalCents,
                prospectsTodayCount: home.prospectsTodayCount,
                prospectsOverdueCount: home.prospectsOverdueCount,
                rankingPosition: home.rankingPosition,
                rankingTotal: home.rankingTotal,
            };
        },
    });
}

/** `soldAt` (ISO) → etiqueta densa del Home: "Hoy 11:20" / "Ayer 18:40" / "12/jul 09:12". */
function saleWhen(soldAt: string): { at: string; atTime: string; today: boolean } {
    // Zona de la tienda, no la del dispositivo: el mismo cobro tiene que decir la misma hora en
    // todos los tablets del piso de venta.
    const hhmm = formatTimeMx(soldAt);
    const days = daysSinceMx(soldAt);
    const label = days === 0 ? 'Hoy' : days === 1 ? 'Ayer' : formatShortDateMx(soldAt);
    return { at: `${label} ${hhmm}`, atTime: hhmm, today: days === 0 };
}

/** Equipo con su capacidad; sin snapshot, el SKU; sin teléfono, «Sin teléfono». */
function equipmentName(sale: SaleSummary): string {
    if (!hasEquipment(sale)) return NO_PHONE_LABEL;
    const model = [sale.productModel, formatStorage(sale.productCapacity)]
        .filter(Boolean)
        .join(' ');
    return itemDisplayName({ sku: sale.sku, brand: sale.productBrand, model });
}

/** Tamaño de página del listado de ventas: menos vueltas para la misma lista. */
const SALES_PAGE_LIMIT = 100;

/**
 * Techo de páginas encadenadas. Al topar, la pantalla lo dice en vez de fingir que ya trajo todo.
 */
const MAX_SALES_PAGES = 20;

export interface RecentSalesResult {
    rows: RecentSale[];
    /** Primera página en vuelo: todavía no hay nada que pintar. */
    isLoading: boolean;
    /** Ya hay filas, pero siguen llegando páginas. */
    isLoadingMore: boolean;
    /** Se llegó al techo de páginas y el silo seguía ofreciendo más. */
    isTruncated: boolean;
    /** La lectura falló. Sin esto, un `/sales` caído se veía igual que un vendedor sin ventas. */
    error: Error | null;
    refetch: () => void;
}

/**
 * Ventas REALES del vendedor (`GET /sales`; el silo acota al scope de su token).
 *
 * 🔴 El listado se trae COMPLETO encadenando `nextToken`, y no por gusto: el silo recorre el índice
 * en orden ASCENDENTE y no acepta `sortOrder`, así que quedarse con la primera página servía las
 * ventas más VIEJAS. Ordenar solo ese tramo dejaba la venta recién cerrada fuera de la pantalla.
 *
 * El fin de la lista lo marca la AUSENCIA de `nextToken`, nunca una página vacía.
 */
const SELLER_SALES_QUERY = infiniteQueryOptions({
    queryKey: PORTAL_KEYS.recentSales,
    queryFn: ({ pageParam }) =>
        wizardApi.listSales({ limit: SALES_PAGE_LIMIT, nextToken: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextToken ?? undefined,
});

const STORE_SALES_QUERY = infiniteQueryOptions({
    queryKey: PORTAL_KEYS.storeSales,
    queryFn: ({ pageParam }) =>
        wizardApi.listStoreSales({ limit: SALES_PAGE_LIMIT, nextToken: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextToken ?? undefined,
});

export function useRecentSales({ enabled = true }: { enabled?: boolean } = {}): RecentSalesResult {
    return useChainedSales(useInfiniteQuery({ ...SELLER_SALES_QUERY, enabled }));
}

/** Ventas de TODA la tienda del vendedor (`GET /sales?index=store`), encadenadas igual. */
export function useStoreSales({ enabled = true }: { enabled?: boolean } = {}): RecentSalesResult {
    return useChainedSales(useInfiniteQuery({ ...STORE_SALES_QUERY, enabled }));
}

type SalesPagesQuery = UseInfiniteQueryResult<InfiniteData<SalesPage>>;

function useChainedSales(query: SalesPagesQuery): RecentSalesResult {
    const pagesFetched = query.data?.pages.length ?? 0;
    const cappedOut = pagesFetched >= MAX_SALES_PAGES;
    const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

    // `pagesFetched` va en las dependencias porque una página que resuelve rápido no alcanza a
    // renderizar el `isFetchingNextPage: true` intermedio, y sin él la cadena se corta en la 2ª.
    useEffect(() => {
        if (hasNextPage && !isFetchingNextPage && !cappedOut) void fetchNextPage();
    }, [hasNextPage, isFetchingNextPage, cappedOut, pagesFetched, fetchNextPage]);

    const rows = useMemo(
        () => toRecentSales(query.data?.pages.flatMap((page) => page.items) ?? []),
        [query.data],
    );

    return {
        rows,
        isLoading: query.isPending,
        isLoadingMore: !query.isPending && hasNextPage && !cappedOut,
        isTruncated: cappedOut && hasNextPage,
        error: asError(query.error),
        refetch: () => {
            void query.refetch();
        },
    };
}

/** El error de una query llega tipado laxo; la UI necesita un `Error` o nada. */
function asError(error: unknown): Error | null {
    if (!error) return null;
    if (error instanceof Error) return error;
    return new Error(typeof error === 'string' ? error : 'Error desconocido');
}

/**
 * Arma los renglones de «Mis ventas» y del Home, de la venta más reciente a la más vieja.
 * El silo devuelve el orden crudo del índice: sin esto, la venta recién cerrada cae donde caiga.
 */
export function toRecentSales(sales: SaleSummary[]): RecentSale[] {
    return [...sales]
        .sort((a, b) => b.soldAt.localeCompare(a.soldAt))
        .map((v) => ({
            saleId: v.saleId,
            saleRef: v.folio ?? v.saleId,
            sku: v.sku,
            customerName: v.customerName ?? '',
            product: equipmentName(v),
            productBrand: v.productBrand,
            productModel: v.productModel,
            productCapacity: v.productCapacity,
            equipmentPriceCents: v.equipmentPriceCents,
            amountCents: v.amountCents,
            soldCents: v.soldCents,
            downPaymentCents: v.downPaymentCents,
            method: v.paymentMethod,
            paymentDetail: v.paymentDetail,
            payments: toPaymentLines(v.payments),
            type: v.type,
            externalProvider: v.externalProvider,
            creditId: v.creditId,
            imei: v.imei,
            retailCustomerId: v.retailCustomerId,
            customerCurp: v.customerCurp ?? null,
            sellerRetailUserId: v.sellerRetailUserId,
            sellerName: v.sellerName,
            storeName: v.storeName,
            zoneName: v.zoneName,
            status: v.status,
            cancelledAt: v.cancelledAt,
            cancelReason: v.cancelReason,
            centralSyncStatus: v.centralSyncStatus,
            soldAt: v.soldAt,
            ...saleWhen(v.soldAt),
            ...(v.lines ? { lines: v.lines } : {}),
            ...(v.salePromotionId !== undefined && { salePromotionId: v.salePromotionId }),
            ...(v.salePromotionVersion !== undefined && {
                salePromotionVersion: v.salePromotionVersion,
            }),
        }));
}

/** Texto por tipo de alerta (presentación; el silo devuelve el `kind`, no la frase). */
const ALERT_TEXT: Record<SellerAlertKind, string> = {
    KYC_REVIEW: 'quedó con el KYC en revisión de Cumplimiento.',
    OTP_FAILED: 'no pudo confirmar el código de verificación.',
};

type SellerAlertKind = SellerHomeTotals['alerts'][number]['kind'];

/**
 * Alertas del piso, informativas: quien resuelve el KYC es Cumplimiento en el backoffice, y el OTP
 * se reintenta dentro del wizard de esa venta — la alerta no trae la sesión para llevar ahí.
 */
export function toSellerAlerts(alerts: SellerHomeTotals['alerts']): SellerAlert[] {
    // El teléfono se agrupa aquí y en ningún otro lado: el Home y «Mi tienda» consumen las mismas
    // alertas, y formatearlo en una sola pantalla lo mostraba de dos formas distintas.
    return alerts.map((a) => ({
        id: a.id,
        customerPhone: formatPhone(a.phone),
        text: ALERT_TEXT[a.kind],
    }));
}

/** Alertas REALES del Home (misma llamada que los KPIs). */
export function useSellerAlerts() {
    return useQuery({
        queryKey: PORTAL_KEYS.sellerAlerts,
        queryFn: () => wizardApi.getSellerHome(),
        select: (home): SellerAlert[] => toSellerAlerts(home.alerts),
    });
}
